import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { debounceTime, firstValueFrom, Subject, Subscription } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { FilterService } from '../../services/filter/filter.service';
import { LoaderService } from '../../services/loader.service';
import { PaginationService } from '../../services/pagination/paginationcard/pagination.service';
import { ModalResidenceCertificateComponent } from '../modal-residence-certificate/modal-residence-certificate.component';

import { TwoFactorAuthService } from '../../services/two-factor/two-factor-auth.service';
import Swal from 'sweetalert2';
import { ProjectsDataService } from '../../services/projects-data.service';
import { FavoritesService } from '../../services/favorites.service';

@Component({
  selector: 'app-cards-real-estate',
  templateUrl: './cards-real-estate.component.html',
  styleUrls: ['./cards-real-estate.component.css'],
})
export class CardsRealEstateComponent
  implements OnInit, OnDestroy, AfterViewChecked
{
  cards: any[] = []; // Todas las propiedades cargadas
  filteredCards: any[] = []; // Propiedades filtradas
  paginatedCards: any[] = []; // Propiedades visibles en la página actual
  isInversor: boolean = false; // Indica si el usuario es inversor
  userWallets: string[] | string = ''; // Wallets del usuario
  loadingButtons: boolean = false; // Indicador para botones de carga
  isLoading: boolean = false; // Indicador de carga
  showPaginationControls: boolean = false; // Controla si mostrar los botones de paginación
  showFilterBar: boolean = false;
  isMobile: boolean = false;
  isTablet: boolean = false;
  currentPage: number = 0;
  totalPages: number = 0;
  itemCount: number = 0;
  name: string = '';
  status: string = '';
  priceRange: [number, number | string] | null = null;
  activationDate: Date | null = null;
  orderBy: string = '';
  startDate: Date | null = null;
  endDate: Date | null = null;
  location: string = '';

  showFiltersModal: boolean = false;
  showFavoritesOnly: boolean = false;

  projectStatusOptions = [
    { value: 'En estudio', label: 'En estudio' },
    { value: 'Activo', label: 'Abierto' },
    { value: 'Vendido', label: 'Vendido' },
    { value: 'Financiado', label: 'Financiado' },
    { value: 'Gestionando reinversiones', label: 'Gestionando reinversiones' },
    { value: 'Repartiendo dividendos', label: 'Repartiendo dividendos' },
  ];

  rentabilidadOptions = [
    { value: 'order_asc', label: 'Orden ascendente' },
    { value: 'order_desc', label: 'Orden descendente' }
  ];

  get locationOptions() {
    const normalize = (s: string) =>
      s.toLowerCase().trim().normalize('NFD').replace(/\p{Diacritic}/gu, '');

    const byKey = new Map<string, string>();
    for (const card of this.cards as any[]) {
      const raw = typeof card.location === 'string' ? card.location.trim() : '';
      if (!raw) continue;
      const key = normalize(raw);
      const existing = byKey.get(key);
      // Preferir la versión con tildes (la "rica") como label canónico
      if (!existing || raw.normalize('NFD').length > existing.normalize('NFD').length) {
        byKey.set(key, raw);
      }
    }

    const uniqueCities = Array.from(byKey.values()).sort((a, b) =>
      a.localeCompare(b, 'es', { sensitivity: 'base' })
    );

    return [
      { value: 'all', label: 'Todas' },
      ...uniqueCities.map(city => ({ value: city, label: city }))
    ];
  }

  /**
   * Resuelve la ciudad de un proyecto priorizando los campos del root y, si están
   * vacíos, cayendo a la primera entrada de `properties[]` (estructura nueva).
   * Mismo patrón que `displayLocation` en card-real-estate.component.ts para que
   * el filtro vea proyectos guardados con la estructura nueva.
   */
  private resolveProjectLocation(dto: any): string {
    if (typeof dto?.city === 'string' && dto.city) return dto.city;
    if (typeof dto?.location === 'string' && dto.location) return dto.location;
    const firstProperty = Array.isArray(dto?.properties) && dto.properties.length > 0
      ? dto.properties[0]
      : null;
    return firstProperty?.location?.city || firstProperty?.city || '';
  }

  toggleFiltersModal() {
    this.showFiltersModal = !this.showFiltersModal;
    this.crf.detectChanges();
  }

  closeFiltersModal() {
    this.showFiltersModal = false;
    this.crf.detectChanges();
  }

  goToFavorites() {
    this.showFavoritesOnly = !this.showFavoritesOnly;
    this.paginationService.setPage(0);
    this.updatePaginatedCards();
  }

  private readonly subscriptions: Subscription = new Subscription(); // Suscripciones para limpiar después
  private resizeHandler: (() => void) | null = null; // Handler para el resize event

  // Propiedades para controlar el estado de carga
  private dataLoaded = false;
  private viewRendered = false;
  private modalChecked = false; // Flag para evitar verificar el modal múltiples veces
  private readonly MODAL_SHOWN_KEY = 'residenceCertificateModalShown'; // Key para sessionStorage

  /** DEBUG: true = forzar modal visible. Poner a false antes de commit. */
  private readonly FORCE_RESIDENCE_MODAL = false;

  constructor(
    private readonly afs: AngularFirestore,
    private readonly authSrv: AuthService,
    private readonly crf: ChangeDetectorRef,
    private readonly paginationService: PaginationService,
    private readonly loaderService: LoaderService,
    private readonly filterService: FilterService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly projectsDataService: ProjectsDataService,
    private readonly twoFactorService: TwoFactorAuthService,
    private readonly favoritesSrv: FavoritesService,
    private el: ElementRef
  ) {}

  private checkScreenSize() {
    const width = window.innerWidth;

    // Definir rangos de dispositivos específicos
    this.isMobile = width <= 576; // Solo móviles pequeños
    this.isTablet = width > 576 && width <= 1366; // Tablets e iPads (ampliado hasta 1366px)

    // Por defecto, ocultar la barra de filtros en móvil y tablet
    if (this.isMobile || this.isTablet) {
      this.showFilterBar = false;
    } else {
      // Mostrar la barra de filtros desktop en pantallas grandes
      this.showFilterBar = true;
    }

    this.crf.detectChanges();
  }

  toggleFilterBar() {
    if (this.isMobile || this.isTablet) {
      this.showFilterBar = !this.showFilterBar;
      this.crf.detectChanges();
    }
  }

  ngAfterViewChecked() {
    if (this.dataLoaded && !this.viewRendered && this.cards.length > 0) {
      if (this.paginatedCards.length > 0) {
        this.viewRendered = true;
        this.checkIfComplete();
      }
    }
  }

  private checkIfComplete() {
    if (!this.dataLoaded) return;

    const hasCards = this.cards.length > 0;
    const hasPaginated = this.paginatedCards.length > 0;

    if (hasCards && hasPaginated) {
      // Dar tiempo extra para que Angular termine de renderizar
      setTimeout(() => {
        this.loaderService.hideSpinner();
        this.isLoading = false;
      }, 500);
    }
  }

  filters$ = new Subject<any>();

  // Método para manejar navegación de vuelta
  onActivate() {
    if (this.cards.length === 0) {
      // Si no hay datos, cargar desde cero
      this.getCards();
    } else {
      // Ya hay datos: asegurarnos de que paginación y spinner se sincronicen
      this.dataLoaded = true;
      this.updatePaginatedCards();

      // Si ya hay tarjetas visibles, consideramos la vista lista
      if (this.paginatedCards.length > 0) {
        this.viewRendered = true;
        this.loaderService.hideSpinner();
        this.isLoading = false;
      } else {
        // Fallback: seguir usando la lógica de checkIfComplete
        this.checkIfComplete();
      }
    }
  }

  ngOnInit(): void {
    // Resetear estados de carga al inicializar
    this.dataLoaded = false;
    this.viewRendered = false;
    this.isLoading = true;

    // ----- Verificación 2FA de entrada -----
    const pendingAuthData = sessionStorage.getItem('pending2FAAuth');
    if (pendingAuthData) {
      setTimeout(() => this.promptForEntryMfa(), 100);
    }
    // ---------------------------------------

    // Configurar el listener de resize
    const resizeHandler = () => this.checkScreenSize();
    window.addEventListener('resize', resizeHandler);

    // Guardar referencia para limpiar después
    this.resizeHandler = resizeHandler;

    this.checkScreenSize();

    // Debug log for initialization
    // console.log('CardsRealEstateComponent initialized');

    // Mostrar spinner de carga inicial inmediatamente
    this.loaderService.showSpinner('Cargando propiedades');

    // Inicializar los favoritos del usuario
    this.subscriptions.add(this.authSrv.userAuth.subscribe((user: any) => {
      if (user?.uid) {
        this.favoritesSrv.loadFavorites(user.uid);
      }
    }));

    // Cargar propiedades desde Firebase
    this.getCards();

    // Configurar la paginación para mostrar 9 propiedades por página
    this.paginationService.setPageSize(9);
    // console.log('Page size set to:', this.paginationService.getPageSize());

    // Escuchar cambios en la página actual
    this.subscriptions.add(
      this.paginationService.currentPage$.subscribe(() => {
        // console.log('Current page changed');
        this.updatePaginatedCards();
      }),
    );

    // Escuchar cambios en el estado del loader
    this.subscriptions.add(
      this.loaderService.dataIsLoading.subscribe((loading) => {
        // Solo actualizar si no estamos en proceso de carga inicial
        if (!this.dataLoaded) {
          this.isLoading = loading?.isLoading ?? false;
        }
        // console.log('Loading state:', this.isLoading);
        this.crf.detectChanges();
      }),
    );

    // Escuchar cambios en los datos filtrados desde el servicio de filtros
    this.subscriptions.add(
      this.filterService.filteredData$.subscribe((filteredData) => {
        // console.log('Filtered data received:', filteredData.length);
        this.filteredCards = filteredData;
        this.updatePaginatedCards(); // Actualizar la paginación con los nuevos datos
      }),
    );
    this.filters$.pipe(debounceTime(300)).subscribe((filters) => {
      this.applyFilters(filters);
    });

    // Verificar si debe mostrarse el modal de certificado de residencia fiscal
    // Solo mostrar una vez por sesión después del login/registro
    const delay = this.FORCE_RESIDENCE_MODAL ? 500 : 2000;
    const shouldCheckModal = () =>
      this.FORCE_RESIDENCE_MODAL ||
      !sessionStorage.getItem(this.MODAL_SHOWN_KEY);

    const currentUser = this.authSrv.userAuth.getValue();
    if (currentUser?.uid && !this.modalChecked) {
      this.modalChecked = true;
      if (shouldCheckModal()) {
        setTimeout(() => this.checkAndShowResidenceCertificateModal(), delay);
      }
    } else {
      this.subscriptions.add(
        this.authSrv.userAuth.subscribe((user: { uid?: string } | null) => {
          if (user && user.uid && !this.modalChecked) {
            this.modalChecked = true;
            if (shouldCheckModal()) {
              setTimeout(() => this.checkAndShowResidenceCertificateModal(), delay);
            }
          }
        }),
      );
    }
  }

  onFilterChange() {
    this.filters$.next({
      name: this.name,
      status: this.status,
      startDate: this.startDate,
      endDate: this.endDate,
      orderBy: this.orderBy,
      location: this.location
    });
  }
  /**
   * Obtiene las propiedades desde Firebase y las envía al servicio de filtros.
   */
  /**
   * Obtiene las propiedades desde ProjectsDataService (API/Firestore) y las envía al servicio de filtros.
   */
  getCards() {
    // Resetear estados de carga
    this.dataLoaded = false;
    this.viewRendered = false;

    // Mostrar spinner inmediatamente
    this.loaderService.showSpinner('Cargando propiedades');
    this.isLoading = true;

    // Usar el servicio unificado (API co fallback a Firestore)
    this.subscriptions.add(
      this.projectsDataService.getProjects().subscribe({
        next: async (res: any) => {
          if (res !== undefined && res !== null) {
            this.isInversor = false;
            const user = await this.authSrv.userAuth.getValue();
            if (user?.uid) {
              const result = await this.afs
                .collection('users')
                .doc(user.uid)
                .get();
              const parseResult: any = await result.toPromise();
              this.isInversor = parseResult?.data()?.isInversorPro ?? false;
            }

            const dataArray = Array.isArray(res) ? res : (res?.data ?? res?.projects ?? []);
            const mappedProjects = dataArray.map((dto: any) => {
              let currentMainImage = dto.mainImage || dto.imageUrl || dto.image;
              if (dto.properties && Array.isArray(dto.properties) && dto.mainPropertyIndex !== undefined) {
                const index = dto.mainPropertyIndex;
                if (dto.properties[index]?.gallery?.length > 0) {
                  currentMainImage = dto.properties[index].gallery[0];
                }
              }

              return {
                ...dto,
                id: dto.id,
                title: dto.title || dto.nameTokens,
                onlyInversors: dto.onlyInversors,
                managingInvestments: dto.managingInvestments ?? dto.managingReinvestments,
                estimatedDeliveryTime: dto.estimatedDeliveryTime || dto.estimatedDeliveryPeriod || null,
                annualReturn: dto.annualReturn || dto.annualProfitability || 0,
                proyectStatus: dto.proyectStatus || dto.status,
                mainImage: currentMainImage,
                priceToken: dto.priceToken || dto.tokenPrice,
                amountToSell: dto.amountToSell,
                amountSold: dto.amountSold,
                reserves: dto.reserves,
                endOfSale: dto.endOfSale || dto.investmentPeriod,
                blockchain: dto.blockchain,
                location: this.resolveProjectLocation(dto),
                nameTokens: dto.nameTokens,
                symbolTokens: dto.symbolTokens || dto.nameTokens,
                street: dto.street,
                propertyType: dto.propertyType,
                properties: dto.properties,
                mainPropertyIndex: dto.mainPropertyIndex
              };
            });

            // Clasificar y ordenar propiedades
            this.cards = mappedProjects.sort(
              (a: any, b: any) => Number(b.id) - Number(a.id),
            );

            this.filterService.loadData(this.cards); // Pasar propiedades al servicio de filtros
            this.updatePaginatedCards(); // Actualizar la paginación inicial

            // Marcar que los datos están cargados
            this.dataLoaded = true;

            // Ocultar spinner inmediatamente después de cargar los datos
            // Usar setTimeout para asegurar que Angular detecte los cambios
            setTimeout(() => {
              this.loaderService.hideSpinner();
              this.isLoading = false;
              this.crf.detectChanges();
              this.checkIfComplete();
            }, 100);
          } else {
            // Si no hay datos, ocultar spinner de todas formas
            this.dataLoaded = true;
            setTimeout(() => {
              this.loaderService.hideSpinner();
              this.isLoading = false;
              this.crf.detectChanges();
            }, 100);
          }
        },
        error: () => {
          this.isLoading = false;
          this.loaderService.hideSpinner();
          this.crf.detectChanges();
        },
      }),
    );
  }
  appyingFilters = false;

  resetFilters() {
    // Mostrar spinner al resetear filtros
    this.loaderService.showSpinner('Reseteando filtros...');
    this.isLoading = true;

    this.name = '';
    this.status = '';
    this.priceRange = null;
    this.activationDate = null;
    this.startDate = null;
    this.endDate = null;
    this.orderBy = '';
    this.location = '';
    this.showFavoritesOnly = false;
    this.applyFilters({ dateRange: null, startDate: null, endDate: null });
    this.appyingFilters = false;
    this.toggleFilterBar();

    // Ocultar spinner después de un breve delay
    setTimeout(() => {
      this.loaderService.hideSpinner();
      this.isLoading = false;
    }, 300);
  }
  /**
   * Aplica los filtros y actualiza la paginación.
   */
  applyFilters(filters: {
    name?: string;
    status?: string;
    // priceRange?: [number, number | string];
    dateRange: Date | null;
    startDate: Date | null;
    endDate: Date | null;
    orderBy?: string;
  }): void {
    // Mostrar spinner cuando se aplican filtros
    this.loaderService.showSpinner('Aplicando filtros...');
    this.isLoading = true;

    this.appyingFilters = true;
    this.filterService.updateFilters(filters); // Actualizamos los filtros
    this.paginationService.setPage(0); // Reseteamos la página a la primera
    this.updatePaginatedCards(); // Actualizamos las propiedades visibles

    // Ocultar spinner después de un pequeño delay
    setTimeout(() => {
      this.loaderService.hideSpinner();
      this.isLoading = false;
    }, 300);
    // this.toggleFilterBar();
  }

  /**
   * Actualiza las propiedades visibles según la paginación y los filtros aplicados.
   */
  private updatePaginatedCards() {
    const startIndex =
      this.paginationService.getCurrentPage() *
      this.paginationService.getPageSize();
    const endIndex = startIndex + this.paginationService.getPageSize();

    // Usar filteredCards si hay datos filtrados; si no, usar cards
    let sourceData = this.appyingFilters ? this.filteredCards : this.cards;

    // Filtro adicional de favoritos
    if (this.showFavoritesOnly) {
      sourceData = sourceData.filter(card => 
        card.id && this.favoritesSrv.isFavorite(card.id)
      );
    }
    // Debug logs for pagination details
    // console.log('Source data total:', sourceData.length);
    // console.log('Page size:', this.paginationService.getPageSize());
    // console.log('Current page:', this.paginationService.getCurrentPage());
    // console.log('Start index:', startIndex);
    // console.log('End index:', endIndex);

    this.paginatedCards = sourceData.slice(startIndex, endIndex); // Propiedades en la página actual

    // console.log('Paginated cards count:', this.paginatedCards.length);

    // Condition to show pagination controls
    this.showPaginationControls =
      sourceData.length > this.paginationService.getPageSize();

    this.itemCount = sourceData.length;
    this.currentPage = this.paginationService.getCurrentPage() + 1;
    this.totalPages = this.getTotalPages();

    // console.log('Show pagination controls:', this.showPaginationControls);
    // console.log(
    //   'Pagination condition:',
    //   sourceData.length,
    //   '>',
    //   this.paginationService.getPageSize()
    // );

    this.crf.detectChanges();
  }

  /**
   * Cambia a la página siguiente.
   */
  private scrollToTop() {
    const s = this.el.nativeElement.closest('.scrollable-container');
    if (s) s.scrollTop = 0;
  }

  goToNextPage() {
    this.loaderService.showSpinner('Cargando página...');
    this.isLoading = true;
    const sourceData = this.filteredCards.length > 0 ? this.filteredCards : this.cards;
    this.paginationService.nextPage(sourceData.length);
    this.updatePaginatedCards();
    this.scrollToTop();
    setTimeout(() => { this.loaderService.hideSpinner(); this.isLoading = false; }, 200);
  }

  goToPreviousPage() {
    this.loaderService.showSpinner('Cargando página...');
    this.isLoading = true;
    this.paginationService.previousPage();
    this.updatePaginatedCards();
    this.scrollToTop();
    setTimeout(() => { this.loaderService.hideSpinner(); this.isLoading = false; }, 200);
  }

  /**
   * Calculates the total number of pages based on the data length and page size.
   */
  getTotalPages(): number {
    let sourceData = this.appyingFilters ? this.filteredCards : this.cards;

    if (this.showFavoritesOnly) {
      sourceData = sourceData.filter(
        card => card.id && this.favoritesSrv.isFavorite(card.id)
      );
    }

    return Math.ceil(sourceData.length / this.paginationService.getPageSize());
  }

  /**
   * Generates an array representing the page numbers.
   */
  getPages(): number[] {
    const totalPages = this.getTotalPages();
    return Array.from({ length: totalPages }, (_, i) => i);
  }

  /**
   * Checks if a given page index is the current page.
   */
  isCurrentPage(pageIndex: number): boolean {
    return this.paginationService.getCurrentPage() === pageIndex;
  }

  /**
   * Navigates to a specific page by index.
   */
  goToPage(pageIndex: number): void {
    this.loaderService.showSpinner('Cargando página...');
    this.isLoading = true;

    this.paginationService.setPage(pageIndex);
    this.updatePaginatedCards();
    this.scrollToTop();

    setTimeout(() => {
      this.loaderService.hideSpinner();
      this.isLoading = false;
    }, 200);
  }

  ngOnDestroy() {
    // Asegurar que el spinner se oculte al destruir el componente
    this.loaderService.hideSpinner();
    this.isLoading = false;

    // Resetear estados de carga
    this.dataLoaded = false;
    this.viewRendered = false;

    // Limpiar todas las suscripciones
    this.subscriptions.unsubscribe();

    // Limpiar el event listener de resize
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }

    // console.log('Component destroyed, subscriptions unsubscribed');
  }

  public isFirstPage(): boolean {
    const isFirst = this.paginationService.getCurrentPage() === 0;
    // console.log('Is first page:', isFirst);
    return isFirst;
  }

  public isLastPage(): boolean {
    const sourceData =
      this.filteredCards.length > 0 ? this.filteredCards : this.cards;
    const isLast =
      (this.paginationService.getCurrentPage() + 1) *
        this.paginationService.getPageSize() >=
      sourceData.length;
    // console.log('Is last page:', isLast);
    return isLast;
  }

  public getCurrentPage(): number {
    const currentPage = this.paginationService.getCurrentPage();
    // console.log('Current page number:', currentPage);
    return currentPage;
  }

  /**
   * Verifica si debe mostrarse el modal de certificado de residencia fiscal
   */
  private async checkAndShowResidenceCertificateModal(): Promise<void> {
    try {
      const user = this.authSrv.userAuth.getValue();
      if (!user || !user.uid) {
        return;
      }

      if (this.FORCE_RESIDENCE_MODAL) {
        setTimeout(() => this.openResidenceCertificateModal(user.uid), 500);
        return;
      }

      const userDoc = await firstValueFrom(
        this.afs.collection('users').doc(user.uid).get(),
      );

      if (!userDoc.exists) {
        return;
      }

      const userData = userDoc.data() as any;

      const kycCompleted = userData?.kyc?.result === 1;
      if (!kycCompleted) {
        return;
      }

      const dontShowValue = userData?.dontShowResidenceCertificateModal;
      const shouldNotShow = dontShowValue === true || dontShowValue === 'true';

      if (shouldNotShow) {
        if (dontShowValue === 'true' && typeof dontShowValue === 'string') {
          try {
            await this.afs.collection('users').doc(user.uid).update({
              dontShowResidenceCertificateModal: false,
            });
          } catch {
            return;
          }
        } else {
          return;
        }
      }

      const userCountry =
        userData?.Empresa?.Datos_Empresa?.Pais_Fiscal ||
        userData?.Empresa?.Datos_Empresa?.pais_de_residencia ||
        userData?.particular?.pais_de_residencia ||
        userData?.Empresa?.Representante?.pais_de_residencia ||
        'España';

      if (userCountry === 'España') {
        return;
      }

      const fiscalDocsSnapshot = await firstValueFrom(
        this.afs
          .collection('fiscal-documents', (ref) =>
            ref.where('userId', '==', user.uid),
          )
          .get(),
      );
      const hasCertificates =
        !fiscalDocsSnapshot.empty && fiscalDocsSnapshot.docs.length > 0;
      if (hasCertificates) {
        return;
      }

      const modalShown = sessionStorage.getItem(this.MODAL_SHOWN_KEY);
      if (modalShown) {
        return;
      }

      setTimeout(() => {
        this.openResidenceCertificateModal(user.uid);
      }, 500);
    } catch {
    }
  }

  /**
   * Abre el modal de certificado de residencia fiscal (con "No volver a mostrar" y dontShowResidenceCertificateModal)
   */
  private openResidenceCertificateModal(userId: string): void {
    try {
      const dialogRef = this.dialog.open(ModalResidenceCertificateComponent, {
        width: '480px',
        maxWidth: '90vw',
        disableClose: false,
        autoFocus: true,
        panelClass: 'modal-residence-certificate',
        data: {
          onDontShowAgain: async (dontShow: boolean) => {
            try {
              await this.afs.collection('users').doc(userId).update({
                dontShowResidenceCertificateModal: dontShow,
              });
            } catch {
              try {
                await this.afs.collection('users').doc(userId).set(
                  {
                    dontShowResidenceCertificateModal: dontShow,
                  },
                  { merge: true },
                );
              } catch {
              }
            }
          },
        },
      });

      sessionStorage.setItem(this.MODAL_SHOWN_KEY, 'true');

      dialogRef.afterClosed().subscribe((result) => {
        if (result && result.dontShowAgain && result.action === 'cancel') {
          // Si canceló y marcó "no volver a mostrar", ya se guardó en onDontShowAgain
        }
      });
    } catch {
    }
  }

  private async promptForEntryMfa(): Promise<void> {
    const result = await Swal.fire({
      title: 'Validación de Seguridad',
      text: 'Tu sesión segura requiere validación. Ingresa tu código TOTP (Google Authenticator) para acceder a la plataforma:',
      input: 'text',
      inputAttributes: {
        autocapitalize: 'off',
        pattern: '[0-9]*',
        inputmode: 'numeric',
        maxlength: '6'
      },
      showCancelButton: true,
      confirmButtonText: 'Verificar y Continuar',
      cancelButtonText: 'Cancelar transacción',
      confirmButtonColor: '#0093A2',
      showLoaderOnConfirm: true,
      backdrop: true,
      allowOutsideClick: false,
      allowEscapeKey: false,
      preConfirm: async (code) => {
        if (!code || code.length < 6) {
          Swal.showValidationMessage('Ingresa los 6 dígitos completos');
          return false;
        }
        try {
          const isValid = await this.twoFactorService.verifyTokenAPI(code);
          if (!isValid) {
            throw new Error('Código incorrecto');
          }
          // Refrescar el token local para obtener nuevos claims y persistir la sesion validada
          try {
             const currentUser = await this.authSrv.userAuth.getValue();
             if (currentUser) await currentUser.getIdToken(true);
             else await this.authSrv.getJWTToken();
          } catch (e) {
             await this.authSrv.getJWTToken();
          }
          return true;
        } catch (err) {
          Swal.showValidationMessage('Código incorrecto o expirado');
          return false;
        }
      }
    });

    if (result.isConfirmed && result.value === true) {
      // Verificación exitosa, limpiar estado temporal
      sessionStorage.removeItem('pending2FAAuth');
      
      const pendingAuthDataStr = sessionStorage.getItem('pending2FAAuth');
      // Aseguramos de completar logicamente el login en authservice si hiciera falta
      // this.authSrv.completeLoginAfter2FA(...); 
      // Dado que authSrv está listo, solo quitamos la barrera y refrescamos los datos if needed.
    } else {
      // Si cancela, lo sacamos de la sesión completamente
      sessionStorage.removeItem('pending2FAAuth');
      this.authSrv.logout();
      this.router.navigateByUrl('/login');
    }
  }
}

